<template>
  <div class="body-text container is-fluid">
    <p>Your application is complete!</p>
    <h2>What now?</h2>
    <p>We'll send you a confirmation email with your membership number within the next three working days.</p>
    <p>You can use this to login to our website and access our member-only resources.</p>
    <p>We'll keep you updated about your application, and if we need any more information, we'll be in touch.</p>
    <div v-if="neverEligible">
      <div v-html="neverEligibleMessage"></div>
    </div>
    <div v-else-if="notEligibleFromEligibleCategory">
      <div v-html="notEligibleFromEligibleCategoryMessage"></div>
    </div>
    <div v-else-if="optedOutOfPli">
      <div v-html="optedOutOfPliConfirmationMessage"></div>
    </div>
    <div v-else-if="optedIntoPli">
      <div v-if="optedIntoPliYesToEither">
        <div v-html="optedIntoPliYesToEitherConfirmationMessage"></div>
      </div>
      <div v-else>
        <div v-html="optedIntoPliYesNeitherConfirmationMessage"></div>
      </div>
    </div>
    <div v-if="!angularAndAppIsLoaded">
      <h2>Update your communications preferences</h2>
      <p>
        Our email communications are the best way to keep up to date with pharmacy news, as well as all of the other benefits
        you get from your RCPharm membership.
      </p>
      <p>
        <a href="/my-rps/my-communication-preferences" class="dnnPrimaryAction">Update your preferences</a>
      </p>
    </div>
    <div v-if="postStudent">
      <h2>Paying by Direct Debit?</h2>
      <p>
        Direct Debit payments are processed through GoCardless. You will receive a confirmation from GoCardless
        confirming that your Direct Debit has been set up. This will be followed by another email 3 working days
        before any new payments are taken from your account, confirming your payment date and amount
      </p>
      <h2>Paying by card?</h2>
      <p data-payemnt-para>Card payments are processed through {{ paymentPlatform }}, and they'll send you an email confirming your payment.</p>
      <h2>Your payment details</h2>
      <p>
        Once you have your website login details you can
        <a href="/my-rps/my-transactions-and-receipts" target="_blank">download payment information</a>.
        Payments are only visible after they've cleared on our system. If you pay by Direct Debit, this will be available approximately
        two working days after the payment has left your account.
      </p>
    </div>
    <p>
      Any questions about your application? Call our membership team on <a href="tel:0333 733 2570"><strong>0333 733 2570</strong></a>
      (lines are open from 9am-5pm, Monday-Friday)
      or email them at <strong><a href="mailto:membership@rcpharm.org" class="mailto-text">membership@rcpharm.org</a></strong>.
    </p>
    <div v-if="postStudent">
      <h2>Support the profession's independent charity</h2>
      <p>
        <img alt="PharmacySupport full logo 2020"
             src="/portals/0/RPS image library/Illustrations/Logos/PharmacySupport full logo 2020.png?ver=2020-02-14-141800-540"
             style="margin: 10px; float: right; height: 129px;" title="PharmacySupport full logo 2020" />
        Since 2019 we have been working closely with the profession's charity
        <a href="https://pharmacistsupport.org/" target="_blank"><strong>Pharmacist Support</strong></a>
        to better understand the mental health and wellbeing of the pharmacy workforce.
        It is through partnerships like these that we can ensure there is relevant
        and accessible support in place for colleagues facing challenging circumstances.
      </p>
      <p>
        As a charity, Pharmacist Support relies on the support of individuals and organisations
        to continue to provide its free and confidential services.
        As a member of the RCPharm, we know you care about our profession, and ask you to join us in
        supporting Pharmacist Support by making a donation.
        Your donation can help change the life of someone facing difficult circumstances,
        including sudden illness or disability, financial hardship,
        or personal crisis by providing practical, emotional and financial help when and where it is needed.
      </p>
      <p>
        <a href="//widgets.justgiving.com/Button/Redirect?p=eyJUZXh0IjoiUGhhcm1hY2lzdCBTdXBwb3J0IiwiSWQiOiIwMTMzODZ
           hNi04ZGY2LTRjODUtODYyOS1jMzI4NmQxMGMzODUiLCJDaGFyaXR5SWQiOjE4NjM5NCwiU2l6ZSInMiLCJSZWZlcmVuY2UiOiJycGhhc
           m1zTWVtYmVycyIsIlR5cGUiOiJDaGFyaXR5RG9uYXRlIn0="
           id="PharmacistSupport" target="_blank">
          <img src="//widgets.justgiving.com/Button?p=eyJUZXh0IjoiUGhhcm1hY2lzdCBTdXBwb3J0IiwiSWQiOiIwMTMzODZhNi04Z
               GY2LTRjODUtODYyOS1jMzI4NmQxMGMzODUiLCJDaGFyaXR5SWQiOjE4NjM5NCwiU2l6ZSI6InMiLCJSZWZlcmVuY2UiOiJycGhhc
               m1zTWVtYmVycyIsIlR5cGUiOiJDaGFyaXR5RG9uYXRlIn0=" alt="" />
        </a>
      </p>
      <p>
        <em>
          For further information on the free and confidential support available through the charity, visit
          <a href="https://pharmacistsupport.org/" target="_blank">pharmacistsupport.org</a>.
        </em>
      </p>
      <p>
        <em>
          To see the impact your donation can make, check out
          <a href="https://pharmacistsupport.org/wp-content/uploads/2022/06/PS-Impact-Report-2021-RGB-FINAL.pdf"
             target="_blank">the charity's latest Impact Report</a>.
        </em>
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import applicationManager from '../applicationManager';
import Ga from '../ga';

export default {
  mounted() {
    Ga.sendConfirmation(this.application.Type);

    if (!this.isUkStudent) {
      Ga.sendTransaction(this.application.Type, this.paymentOption.TotalAmountDue, this.transactionId);
    }

    applicationManager
      .postApplicationComplete()
      .then(() => {
        this.disableProgressBar(false);

        if (this.angularAndAppIsLoaded) {
          const openCommunitcationsEvent = new Event('RPS:CommsPrefs:Show');
          document.dispatchEvent(openCommunitcationsEvent);
        }

        if (process.env.NODE_ENV === 'development') {
          window.sessionStorage.removeItem('dnn-user');
        }
      });
  },
  methods: {
    ...mapMutations(['disableProgressBar']),
  },
  computed: {
    ...mapGetters(['transactionId', 'getIsOverseas', 'optedOutOfPli', 'optedOutOfPliConfirmationMessage', 'optedIntoPli',
      'optedIntoPliYesToEitherConfirmationMessage', 'notEligibleFromEligibleCategory', 'optedIntoPliYesToEither',
      'notEligibleFromEligibleCategoryMessage', 'neverEligible', 'neverEligibleMessage', 'optedIntoPliYesNeitherConfirmationMessage']),
    ...mapState(['application', 'braintreeOptions', 'paymentOption']),
    postStudent() {
      return this.application.Type !== 'Student';
    },
    student() {
      return this.application.Type === 'Student';
    },
    isUkStudent() {
      return !this.getIsOverseas && this.student;
    },
    paymentPlatform() {
      return this.braintreeOptions.useFeature ? 'Braintree' : 'WorldPay';
    },
    angularAndAppIsLoaded() {
      return window.angular && document.getElementById('comms-pref-app');
    },
  },
};
</script>

<style lang="scss">
@import '../styles/_mixins.scss';
  div.body-text p {
    @include DefaultPoppinFont();
  }
  div.body-text{
      padding-bottom: 20px;
  }
  div.mobile-view {
      div.body-text {
         a.mailto-text {
            font-size: 19px;
         }
      }
  }
</style>
